import { useEffect, useState } from "react";
import { Loader2, MessagesSquare, RefreshCw, BadgeCheck, Sparkles, Timer } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/States";
import { SelectionForm } from "@/components/SelectionForm";
import { UpgradeNote } from "@/components/UpgradeNote";
import { usePlan } from "@/lib/plan";
import InterviewCard from "@/components/InterviewCard";
import { api, fetchSubjects, formatError, runJob } from "@/lib/api";

function RecentSets({ items, onOpen }) {
  if (!items.length) return <EmptyState testId="interview-recent-empty" title="No interview sets yet" text="Generate your first set above. Sets you practise are saved to your account." />;
  return (
    <div data-testid="interview-recent" className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((s) => (
        <button key={s.id} data-testid={`interview-set-${s.id}`} onClick={() => onOpen(s.id)} className="card-hover rounded-xl border border-line bg-surface p-4 text-left">
          <p className="text-xs font-semibold uppercase tracking-wider text-aqua">{s.subject_name}</p>
          <p className="mt-1 font-semibold text-ink">{s.topic || "Mixed topics"}</p>
          <p className="mt-1 text-xs text-ink-3"><span className="capitalize">{s.difficulty}</span> · {s.confident} confident · {s.practise} to practise</p>
        </button>
      ))}
    </div>
  );
}

export default function Interview() {
  const { limits } = usePlan();
  const [subjects, setSubjects] = useState(null);
  const [sel, setSel] = useState({ subject_id: "", topic: "", difficulty: "beginner" });
  const [set, setSet] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [timeLimit, setTimeLimit] = useState(null);
  const loadRecent = () => api.get("/interview/mine").then((r) => setRecent(r.data)).catch(() => {});
  useEffect(() => { fetchSubjects().then(setSubjects).catch((e) => setErr(formatError(e))); loadRecent(); }, []);

  const generate = async (regenerate = false) => {
    const body = regenerate && set ? { subject_id: set.subject_id, topic: set.topic, difficulty: set.difficulty } : sel;
    setBusy(true); setErr("");
    try { setSet(await runJob("/interview/generate", { ...body, regenerate })); loadRecent(); } catch (e) { setErr(formatError(e)); }
    setBusy(false);
  };
  const open = async (id) => {
    setErr("");
    try {
      const { data } = await api.get(`/interview/sets/${id}`);
      setSet(data);
      setSel({ subject_id: data.subject_id, topic: data.topic, difficulty: data.difficulty });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { setErr(formatError(e)); }
  };
  const onRate = (i, r) => setSet((s) => {
    const ratings = { ...s.ratings };
    if (r === "none") delete ratings[String(i)]; else ratings[String(i)] = r;
    return { ...s, ratings };
  });

  if (!subjects) return err ? <ErrorState message={err} /> : <Loading />;
  const vals = set ? Object.values(set.ratings) : [];

  return (
    <div>
      <PageHeader eyebrow="Interview prep" title="Practise for your next interview" text="Realistic clinical research interview questions with model answers, what interviewers listen for, and likely follow-ups." />
      <div className="rounded-2xl border border-line bg-surface p-6">
        {limits && !limits.interview && <div className="mb-5"><UpgradeNote testId="interview-locked" text="Interview preparation is included in LearnAI Plus and Pro." /></div>}
        <SelectionForm limits={limits} subjects={subjects} value={sel} onChange={setSel} topicRequired={false} />
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Button data-testid="interview-generate-btn" disabled={!sel.subject_id || busy || (limits && !limits.interview)} onClick={() => generate(false)} className="rounded-full">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessagesSquare className="mr-2 h-4 w-4" />}Get interview questions
          </Button>
          {set && !busy && <Button data-testid="interview-new-set-btn" variant="outline" onClick={() => generate(true)} className="rounded-full"><RefreshCw className="mr-2 h-4 w-4" />Generate a fresh set</Button>}
          <div className="ml-auto flex items-center gap-3 rounded-full border border-line px-4 py-1.5">
            <Timer className="h-4 w-4 text-ink" />
            <label htmlFor="timed-toggle" className="text-sm font-medium text-ink">Timed mode{limits && !limits.mock_tests && <span data-testid="interview-timed-pro" className="ml-1.5 rounded-full bg-aqua-soft px-2 py-0.5 text-[10px] font-semibold text-aqua">Pro</span>}</label>
            <Switch id="timed-toggle" data-testid="interview-timed-toggle" checked={!!timeLimit} disabled={!!limits && !limits.mock_tests} onCheckedChange={(v) => setTimeLimit(v ? 90 : null)} />
            {timeLimit && (
              <Select value={String(timeLimit)} onValueChange={(v) => setTimeLimit(Number(v))}>
                <SelectTrigger data-testid="interview-time-select" className="h-8 w-28"><SelectValue /></SelectTrigger>
                <SelectContent>{[60, 90, 120, 180].map((s) => <SelectItem key={s} data-testid={`interview-time-${s}`} value={String(s)}>{s >= 120 ? `${s / 60} min` : `${s} sec`}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>
      {err && <div className="mt-6"><ErrorState message={err} /></div>}
      {busy && <Loading label="Preparing interview questions and model answers (about 30–60 seconds)..." />}
      {!busy && set && (
        <div data-testid="interview-set" className="mt-8">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-aqua">{set.subject_name} · <span className="capitalize">{set.difficulty}</span></p>
              <h2 className="text-2xl font-extrabold text-ink">{set.topic || "Mixed topics"}</h2>
              <span data-testid="interview-set-status" className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${set.status === "approved" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-line bg-canvas-2 text-ink-2"}`}>
                {set.status === "approved" ? <><BadgeCheck className="h-3.5 w-3.5" />Expert-reviewed by IDDCR</> : <><Sparkles className="h-3.5 w-3.5" />AI-generated · pending expert review</>}
              </span>
            </div>
            <p data-testid="interview-progress" className="text-sm text-ink-3"><b className="text-emerald-700">{vals.filter((v) => v === "confident").length}</b> confident · <b className="text-amber-700">{vals.filter((v) => v === "practise").length}</b> to practise · {set.questions.length} questions</p>
          </div>
          <div className="flex flex-col gap-4">
            {set.questions.map((q, i) => <InterviewCard key={`${set.id}-${i}`} setId={set.id} q={q} index={i} rating={set.ratings[String(i)]} onRate={onRate}
              feedback={set.feedback?.[String(i)]} timeLimit={timeLimit} feedbackLocked={!!limits && !limits.interview_feedback} onFeedback={(idx, fb) => setSet((s) => ({ ...s, feedback: { ...(s.feedback || {}), [String(idx)]: fb } }))} />)}
          </div>
          <p className="mt-6 text-xs text-ink-3">AI-generated content for practice. Verify regulatory specifics against official guidance before interviews.</p>
        </div>
      )}
      <h3 className="mb-4 mt-12 text-xl font-bold text-ink">Your interview sets</h3>
      <RecentSets items={recent} onOpen={open} />
    </div>
  );
}
